import { useEffect, useState } from 'react';
import { X, Sparkles } from 'lucide-react';

import { useApi } from '../api-context';

/**
 * The settings a photographer can change, which is currently one.
 *
 * It exists because the Review Assist popup offers "do this for every shoot",
 * and an option that can be switched on and never off is a trap rather than a
 * convenience. Anything added here later should earn its place the same way.
 */
export default function SettingsPanel({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const [always, setAlways] = useState(false);

  useEffect(() => setAlways(api.assistAlways()), [api]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [saving, setSaving] = useState(false);

  const toggle = async (value: boolean) => {
    setAlways(value);
    setSaving(true);
    // Awaited so that closing the panel and the tab in the same second cannot
    // lose the answer.
    try { await api.setAssistAlways(value); } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/80 p-5 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-ink-800 bg-ink-900 p-7 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="settings-title" className="text-head font-semibold text-ink-50">
            Settings
          </h2>
          <button
            className="-mr-1 -mt-1 rounded-lg p-1.5 text-ink-500 transition hover:bg-ink-850 hover:text-ink-200"
            onClick={onClose}
            aria-label="Close settings"
          >
            <X size={16} />
          </button>
        </div>

        <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-ink-800 bg-ink-850/40 p-4">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink-700 bg-ink-850 accent-ink-100"
            checked={always}
            onChange={(event) => void toggle(event.target.checked)}
          />
          <span>
            <span className="flex items-center gap-1.5 text-body text-ink-100">
              <Sparkles size={14} className="text-warn" />
              Run Review Assist on every shoot
            </span>
            <span className="mt-1.5 block text-caption leading-relaxed text-ink-500">
              Groups your bursts and marks the sharpest of each as soon as photos are
              imported, without asking first. Runs on your machine; nothing is uploaded.
            </span>
          </span>
        </label>

        <p className="mt-5 text-caption leading-relaxed text-ink-500">
          Shoots you have already answered for keep their answer. Turning this off only
          means you will be asked again next time.
        </p>

        <div className="mt-7 flex justify-end">
          <button className="btn-primary px-5 py-2.5 text-body" onClick={onClose} disabled={saving}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
